import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar from "../components/Sidebar";
import {
  Dashboard6Styles, ModalShell, Field, PagePreloader, Avatar, StatusChip, CardHead,
} from "../components/ModalUI";
import {
  INSTITUTIONS_LIST,
  POPULAR_BANKS_LIST,
  STATES_LIST,
  KATSINA_LGAS,
  ACADEMIC_SESSIONS_LIST,
  LEVELS_LIST,
  SCHOLARSHIP_DURATIONS_LIST,
  DEFAULT_MOCK_SCHOLARSHIPS,
  DEFAULT_MOCK_PAYMENTS,
  normalizeScholarship
} from "../data/mockScholarships";

const STORAGE_KEY = "zangon_scholarships";
const PAYMENTS_KEY = "zangon_scholarship_payments";

function toTitleCase(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return String(iso);
  }
}

function fmtDateLong(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  } catch {
    return String(iso);
  }
}

function fmtCurrency(amount) {
  return `₦${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
}

export default function ScholarshipDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pageReady, setPageReady] = useState(false);
  const [scholarships, setScholarships] = useState([]);
  const [payments, setPayments] = useState([]);

  // Modal states
  const [disburseAmount, setDisburseAmount] = useState("");
  const [editForm, setEditForm] = useState({
    serial_number: "",
    academic_session: "2025/2026",
    institution_name: "",
    custom_institution_name: "",
    student_name: "",
    registration_number: "",
    state_of_origin: "Katsina",
    lga: "Zango",
    course: "",
    level: "200",
    scholarship_duration: "4 yrs",
    year_of_entry: 2024,
    year_of_graduation: 2028,
    tuition_or_reg_fees: "",
    allowance: "",
    total_amount: "",
    bank_name: "Zenith Bank",
    custom_bank_name: "",
    account_number: "",
    remark: "",
    phone: "",
    email: "",
    imageUrl: "",
    imagePreview: null
  });

  const editModalRef = useRef(null);
  const editFileInputRef = useRef(null);
  const disburseModalRef = useRef(null);
  const deleteModalRef = useRef(null);

  // Load from local storage or seed default mock data
  const loadData = () => {
    const localSch = localStorage.getItem(STORAGE_KEY);
    const localPay = localStorage.getItem(PAYMENTS_KEY);

    let loadedSch = [];
    if (localSch) {
      try {
        const parsed = JSON.parse(localSch);
        if (Array.isArray(parsed) && parsed.length > 0) {
          loadedSch = parsed;
        }
      } catch {
        loadedSch = [];
      }
    }

    const existingIds = new Set(loadedSch.map(s => s.id));
    const toAdd = DEFAULT_MOCK_SCHOLARSHIPS.filter(s => !existingIds.has(s.id));
    const mergedSch = (loadedSch.length === 0 ? DEFAULT_MOCK_SCHOLARSHIPS : [...loadedSch, ...toAdd]).map(
      (s, idx) => normalizeScholarship(s, idx)
    );

    localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedSch));
    setScholarships(mergedSch);

    let loadedPay = [];
    if (localPay) {
      try {
        const parsed = JSON.parse(localPay);
        if (Array.isArray(parsed) && parsed.length > 0) {
          loadedPay = parsed;
        }
      } catch {
        loadedPay = [];
      }
    }
    if (loadedPay.length === 0) {
      loadedPay = DEFAULT_MOCK_PAYMENTS;
      localStorage.setItem(PAYMENTS_KEY, JSON.stringify(DEFAULT_MOCK_PAYMENTS));
    }
    setPayments(loadedPay);
  };

  // Initialize Metronic components & theme mode
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest("[data-kt-value]");
      if (!el) return;
      const mode = el.getAttribute("data-kt-value");
      if (mode) {
        if (window.KTThemeMode?.setMode) window.KTThemeMode.setMode(mode);
        else {
          document.documentElement.setAttribute("data-bs-theme", mode);
          localStorage.setItem("kt_theme_mode", mode);
        }
      }
    };
    document.addEventListener("click", onClick);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };

    init();
    const t = setTimeout(init, 0);
    return () => {
      document.removeEventListener("click", onClick);
      clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    loadData();
    const t = setTimeout(() => setPageReady(true), 300);
    return () => clearTimeout(t);
  }, [id]);

  const scholarship = useMemo(() => {
    return scholarships.find(s => s.id === id) || null;
  }, [scholarships, id]);

  const studentPayments = useMemo(() => {
    return payments
      .filter(p => p.scholarshipId === id)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [payments, id]);

  // Toggle status (Pause/Resume)
  const handleToggleStatus = () => {
    if (!scholarship) return;
    const nextStatus = scholarship.status === "Active" ? "Paused" : "Active";
    const updated = scholarships.map(s => {
      if (s.id === id) {
        return { ...s, status: nextStatus };
      }
      return s;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setScholarships(updated);
    toast.success(`Scholarship funding status updated to ${nextStatus}`);
  };

  // Image Selection Handler
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size must be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEditForm(prev => ({
        ...prev,
        imageUrl: ev.target.result,
        imagePreview: ev.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // Open Edit Modal
  const handleOpenEdit = () => {
    if (!scholarship) return;
    const isKnownInst = INSTITUTIONS_LIST.includes(scholarship.institution_name);
    const isKnownBank = POPULAR_BANKS_LIST.includes(scholarship.bank_name);

    setEditForm({
      serial_number: scholarship.serial_number || "",
      academic_session: scholarship.academic_session || "2025/2026",
      institution_name: isKnownInst ? scholarship.institution_name : "Other / External Institution",
      custom_institution_name: isKnownInst ? "" : scholarship.institution_name || "",
      student_name: scholarship.student_name || scholarship.name || "",
      registration_number: scholarship.registration_number || "",
      state_of_origin: scholarship.state_of_origin || "Katsina",
      lga: scholarship.lga || "Zango",
      course: scholarship.course || "",
      level: String(scholarship.level || "200").replace(/\D/g, "") || "200",
      scholarship_duration: scholarship.scholarship_duration || "4 yrs",
      year_of_entry: scholarship.year_of_entry || 2024,
      year_of_graduation: scholarship.year_of_graduation || 2028,
      tuition_or_reg_fees: scholarship.tuition_or_reg_fees ?? scholarship.schoolFeesAmount ?? "",
      allowance: scholarship.allowance ?? scholarship.allowanceAmount ?? "",
      total_amount: scholarship.total_amount ?? scholarship.amount ?? "",
      bank_name: isKnownBank ? scholarship.bank_name : "Other",
      custom_bank_name: isKnownBank ? "" : scholarship.bank_name || "",
      account_number: scholarship.account_number || scholarship.bankAccount || "",
      remark: scholarship.remark || "",
      phone: scholarship.phone || "",
      email: scholarship.email || "",
      imageUrl: scholarship.imageUrl || "",
      imagePreview: scholarship.imageUrl || null
    });

    if (window.bootstrap?.Modal && editModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(editModalRef.current).show();
    }
  };

  // Submit Edit Details
  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!scholarship) return;

    const finalInstitution = editForm.institution_name === "Other / External Institution"
      ? (editForm.custom_institution_name || "").trim()
      : editForm.institution_name;

    const finalBank = editForm.bank_name === "Other"
      ? (editForm.custom_bank_name || "").trim()
      : editForm.bank_name;

    const tuition = Number(editForm.tuition_or_reg_fees) || 0;
    const allowance = Number(editForm.allowance) || 0;
    const computedTotal = tuition + allowance;

    if (!editForm.student_name.trim()) {
      toast.error("Please enter student's full name.");
      return;
    }
    if (!finalInstitution) {
      toast.error("Please enter an institution name.");
      return;
    }
    if (computedTotal <= 0) {
      toast.error("Please enter valid tuition or allowance amounts.");
      return;
    }

    const updated = scholarships.map(s => {
      if (s.id === id) {
        return normalizeScholarship({
          ...s,
          serial_number: editForm.serial_number.trim() || s.serial_number,
          academic_session: editForm.academic_session,
          institution_name: finalInstitution,
          student_name: editForm.student_name.trim(),
          registration_number: editForm.registration_number.trim().toUpperCase(),
          state_of_origin: editForm.state_of_origin,
          lga: editForm.lga,
          course: editForm.course.trim(),
          level: editForm.level,
          scholarship_duration: editForm.scholarship_duration,
          year_of_entry: Number(editForm.year_of_entry) || s.year_of_entry,
          year_of_graduation: Number(editForm.year_of_graduation) || s.year_of_graduation,
          tuition_or_reg_fees: tuition,
          allowance: allowance,
          total_amount: computedTotal,
          bank_name: finalBank,
          account_number: editForm.account_number.trim(),
          remark: editForm.remark.trim(),
          phone: editForm.phone || s.phone,
          email: editForm.email || s.email,
          imageUrl: editForm.imageUrl || s.imageUrl || ""
        });
      }
      return s;
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setScholarships(updated);
    toast.success("Scholarship profile updated successfully!");

    if (window.bootstrap?.Modal && editModalRef.current) {
      window.bootstrap.Modal.getInstance(editModalRef.current)?.hide();
    }
  };

  // Open Disburse Modal
  const handleOpenDisburse = () => {
    if (!scholarship) return;
    setDisburseAmount(scholarship.total_amount || scholarship.amount || "");
    if (window.bootstrap?.Modal && disburseModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(disburseModalRef.current).show();
    }
  };

  // Submit Disburse
  const handleDisburseSubmit = (e) => {
    e.preventDefault();
    if (!scholarship) return;

    const payoutVal = Number(disburseAmount);
    if (!payoutVal || payoutVal <= 0) {
      toast.error("Please enter a valid payout amount");
      return;
    }

    // Update scholarship total disbursed
    const updatedSchs = scholarships.map(s => {
      if (s.id === id) {
        return {
          ...s,
          totalDisbursed: (Number(s.totalDisbursed) || 0) + payoutVal
        };
      }
      return s;
    });

    // Append payment history record
    const ref = "SCH-PAY-" + Math.floor(10000 + Math.random() * 90000);
    const newPayment = {
      id: "pay_" + Date.now(),
      scholarshipId: id,
      studentName: scholarship.student_name || scholarship.name,
      amount: payoutVal,
      date: new Date().toISOString(),
      status: "Completed",
      reference: ref,
      method: "Direct Bank Transfer",
      bankName: scholarship.bank_name,
      bankAccount: scholarship.account_number,
      notes: `${scholarship.academic_session} Session Grant Payout (${scholarship.registration_number})`
    };
    const updatedPayments = [newPayment, ...payments];

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSchs));
    localStorage.setItem(PAYMENTS_KEY, JSON.stringify(updatedPayments));

    setScholarships(updatedSchs);
    setPayments(updatedPayments);

    toast.success(`Disbursed ${fmtCurrency(payoutVal)} successfully!`);
    if (window.bootstrap?.Modal && disburseModalRef.current) {
      window.bootstrap.Modal.getInstance(disburseModalRef.current)?.hide();
    }
  };

  // Open Delete Modal
  const handleOpenDelete = () => {
    if (window.bootstrap?.Modal && deleteModalRef.current) {
      window.bootstrap.Modal.getOrCreateInstance(deleteModalRef.current).show();
    }
  };

  // Submit Delete
  const handleDeleteSubmit = () => {
    const updatedSchs = scholarships.filter(s => s.id !== id);
    const updatedPayments = payments.filter(p => p.scholarshipId !== id);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSchs));
    localStorage.setItem(PAYMENTS_KEY, JSON.stringify(updatedPayments));

    toast.success("Scholarship profile terminated permanently");
    if (window.bootstrap?.Modal && deleteModalRef.current) {
      window.bootstrap.Modal.getInstance(deleteModalRef.current)?.hide();
    }
    navigate("/scholarship");
  };

  const hideModal = (ref) => {
    if (window.bootstrap?.Modal && ref.current) window.bootstrap.Modal.getInstance(ref.current)?.hide();
  };

  if (pageReady && !scholarship) {
    return (
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid align-items-center justify-content-center py-20">
                <i className="ki-outline ki-cross-circle fs-5x text-danger mb-4"></i>
                <h3 className="fw-bold text-gray-900">Scholarship Record Not Found</h3>
                <p className="text-muted fs-6 mb-6">The student profile does not exist or has been terminated.</p>
                <Link to="/scholarship" className="btn btn-primary btn-sm">
                  Back to Scholarships
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const scholarName = scholarship?.student_name || scholarship?.name || "Scholar Profile";
  const tuitionAmt = Number(scholarship?.tuition_or_reg_fees) || 0;
  const allowanceAmt = Number(scholarship?.allowance) || 0;
  const totalAmt = Number(scholarship?.total_amount) || (tuitionAmt + allowanceAmt);

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          {/* Header */}
          <div
            id="kt_app_header"
            className="app-header"
            data-kt-sticky="true"
            data-kt-sticky-activate="{default: false, lg: true}"
            data-kt-sticky-name="app-header-sticky"
            data-kt-sticky-offset="{default: false, lg: '300px'}"
          >
            <div
              className="app-container container-fluid d-flex flex-stack align-items-center my-6"
              id="kt_app_header_container"
            >
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div
                  className="btn btn-icon btn-active-color-primary w-35px h-35px me-2"
                  id="kt_app_sidebar_mobile_toggle"
                >
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>

              <div className="d-flex flex-stack flex-lg-row-fluid" id="kt_app_header_wrapper">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3 mb-0">
                  <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-2">
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">
                      <Link to="/dashboard" className="text-gray-700 text-hover-primary me-1">
                        <i className="ki-outline ki-home text-gray-700 fs-6"></i>
                      </Link>
                    </li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">
                      <Link to="/scholarship" className="text-gray-700 text-hover-primary">Scholarships</Link>
                    </li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">{toTitleCase(scholarName)}</li>
                  </ul>
                  <div className="d-flex align-items-center gap-3">
                    <h1 className="text-gray-900 d6-page-title m-0">Scholarship Profile</h1>
                    <span className="badge badge-light-primary fw-bolder fs-8 px-2 py-1">
                      {scholarship?.serial_number || "S/N"}
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                    onClick={handleToggleStatus}
                  >
                    <i className={`ki-outline ${scholarship?.status === "Active" ? "ki-pause" : "ki-play"} fs-6 me-sm-2`}></i>
                    <span className="d-none d-sm-inline">{scholarship?.status === "Active" ? "Pause Funding" : "Resume Funding"}</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                    onClick={handleOpenEdit}
                  >
                    <i className="ki-outline ki-pencil fs-6 me-sm-2"></i>
                    <span className="d-none d-sm-inline">Edit Profile</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                    onClick={handleOpenDisburse}
                    disabled={scholarship?.status !== "Active"}
                  >
                    <i className="ki-outline ki-wallet fs-6 me-sm-2 text-white"></i>
                    <span className="d-none d-sm-inline">Disburse Payout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="row g-5">

                      {/* LEFT COLUMN: Student Bio-Data, Banking & Metadata */}
                      <div className="col-12 col-xl-4">
                        <div className="d-flex flex-column gap-5">

                          {/* Profile Header Card */}
                          <div className="d6-card p-6 overflow-hidden position-relative">
                            <div className="d-flex align-items-center gap-4 mb-4">
                              <Avatar src={scholarship?.imageUrl} name={scholarName} size={72} />
                              <div className="d-flex flex-column min-w-0">
                                <span className="badge badge-light-primary fw-bolder fs-8 align-self-start mb-1">
                                  {scholarship?.serial_number || "S/N"}
                                </span>
                                <h3 className="text-gray-900 fw-bold fs-4 m-0 text-truncate">
                                  {toTitleCase(scholarName)}
                                </h3>
                                <span className="text-primary fw-semibold fs-7 mt-1">
                                  {scholarship?.registration_number}
                                </span>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-4">
                              <StatusChip color={scholarship?.status === "Active" ? "#16A34A" : scholarship?.status === "Paused" ? "#D97706" : "#2563EB"}>
                                {scholarship?.status}
                              </StatusChip>
                              <span className="badge bg-light-info text-info fw-bold fs-8 rounded-pill px-3">
                                {scholarship?.academic_session} Session
                              </span>
                            </div>

                            <div className="separator my-4"></div>

                            {/* Student Bio-Data & Origin */}
                            <div className="mb-4">
                              <div className="mb-3">
                                <span className="fw-bolder text-gray-900 fs-7">Student Bio-Data &amp; Origin</span>
                              </div>
                              <div className="d-flex flex-column gap-2 fs-7">
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">Full Name</span>
                                  <span className="fw-bold text-gray-900 text-end">{scholarName}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">State of Origin</span>
                                  <span className="fw-semibold text-gray-900">{scholarship?.state_of_origin} State</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">LGA</span>
                                  <span className="fw-semibold text-gray-900">{scholarship?.lga}</span>
                                </div>
                                {scholarship?.phone && scholarship?.phone !== "—" && (
                                  <div className="d-flex justify-content-between">
                                    <span className="text-gray-500">Phone</span>
                                    <span className="fw-semibold text-gray-900">{scholarship?.phone}</span>
                                  </div>
                                )}
                                {scholarship?.email && (
                                  <div className="d-flex justify-content-between">
                                    <span className="text-gray-500">Email</span>
                                    <span className="fw-semibold text-gray-900 text-break text-end ps-2">{scholarship?.email}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="separator my-4"></div>

                            {/* Disbursement & Banking Details */}
                            <div className="mb-4">
                              <div className="mb-3">
                                <span className="fw-bolder text-gray-900 fs-7">Disbursement &amp; Banking Details</span>
                              </div>
                              <div className="p-3 bg-light rounded-3 d-flex flex-column gap-2 fs-7">
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">Bank Name</span>
                                  <span className="fw-bold text-gray-900">{scholarship?.bank_name}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">Account Number</span>
                                  <span className="fw-bold text-primary fs-6">{scholarship?.account_number}</span>
                                </div>
                              </div>
                            </div>

                            <div className="separator my-4"></div>

                            {/* Administrative & Metadata */}
                            <div className="mb-4">
                              <div className="mb-3">
                                <span className="fw-bolder text-gray-900 fs-7">Administrative &amp; Metadata</span>
                              </div>
                              <div className="d-flex flex-column gap-2 fs-7">
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">Serial Number (S/N)</span>
                                  <span className="badge badge-light-primary fw-bold">{scholarship?.serial_number}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span className="text-gray-500">Awarded Date</span>
                                  <span className="fw-semibold text-gray-900">{fmtDateLong(scholarship?.createdDate)}</span>
                                </div>
                                <div className="mt-2">
                                  <span className="text-gray-500 d-block fs-8 mb-1">Remark</span>
                                  <div className="p-3 bg-light rounded-3 text-gray-700 fs-8 fst-italic">
                                    &ldquo;{scholarship?.remark || "Active scholar in good academic standing."}&rdquo;
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="separator my-4"></div>

                            {/* Terminate Action */}
                            <div className="d6m-note d6m-note-danger d-flex flex-column p-4">
                              <div className="mb-3 fs-8 text-gray-700">
                                Terminating funding permanently marks this beneficiary profile as removed.
                              </div>
                              <button type="button" className="btn btn-danger d6-mbtn w-100" onClick={handleOpenDelete}>
                                <i className="ki-outline ki-trash fs-5 me-2"></i>Terminate Scholarship
                              </button>
                            </div>

                          </div>
                        </div>
                      </div>

                      {/* RIGHT COLUMN: Academic Info, Financial Breakdown & History */}
                      <div className="col-12 col-xl-8 d-flex flex-column gap-5">

                        {/* Financial & Fee Breakdown Card */}
                        <div className="d6-card p-6">
                          <CardHead
                            title="Financial & Fee Breakdown"
                            subtitle="Tuition, living allowance, and cumulative disbursement tracking"
                            className="mb-4"
                          />
                          <div className="row g-4">
                            <div className="col-12 col-sm-6 col-lg-3">
                              <div className="d6m-invoice h-100 text-center p-4">
                                <span className="d6m-k d-block">Tuition / Reg Fees</span>
                                <span className="d6-title text-gray-800 fs-3 d-block my-1">{fmtCurrency(tuitionAmt)}</span>
                                <span className="d6m-muted fs-9">School / Institutional Fee</span>
                              </div>
                            </div>
                            <div className="col-12 col-sm-6 col-lg-3">
                              <div className="d6m-invoice h-100 text-center p-4">
                                <span className="d6m-k d-block">Allowance</span>
                                <span className="d6-title text-gray-800 fs-3 d-block my-1">{fmtCurrency(allowanceAmt)}</span>
                                <span className="d6m-muted fs-9">Living / Stipend Support</span>
                              </div>
                            </div>
                            <div className="col-12 col-sm-6 col-lg-3">
                              <div className="d6m-invoice h-100 text-center p-4 bg-light-success">
                                <span className="d6m-k d-block text-success fw-bold">Total Scholarship</span>
                                <span className="d6-title text-success fs-3 d-block my-1">{fmtCurrency(totalAmt)}</span>
                                <span className="d6m-muted fs-9 text-success">Tuition + Allowance</span>
                              </div>
                            </div>
                            <div className="col-12 col-sm-6 col-lg-3">
                              <div className="d6m-invoice h-100 text-center p-4">
                                <span className="d6m-k d-block">Total Disbursed</span>
                                <span className="d6-title text-primary fs-3 d-block my-1">{fmtCurrency(scholarship?.totalDisbursed)}</span>
                                <span className="d6m-muted fs-9">Paid to beneficiary</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* General & Academic Information Card */}
                        <div className="d6-card p-6">
                          <CardHead
                            title="General & Academic Information"
                            subtitle="Current academic enrollment, institutional registry, and course progression"
                            className="mb-4"
                          />
                          <div className="row g-4">
                            <div className="col-12 col-md-6">
                              <div className="p-3 bg-light rounded-3 d-flex justify-content-between align-items-center">
                                <span className="text-gray-500 fs-7">Academic Session</span>
                                <span className="fw-bolder text-gray-900 fs-6">{scholarship?.academic_session} Session</span>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="p-3 bg-light rounded-3 d-flex justify-content-between align-items-center">
                                <span className="text-gray-500 fs-7">Institution / School</span>
                                <span className="fw-bolder text-gray-900 fs-7 text-end ps-2 text-truncate" title={scholarship?.institution_name}>
                                  {scholarship?.institution_name}
                                </span>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="p-3 bg-light rounded-3 d-flex justify-content-between align-items-center">
                                <span className="text-gray-500 fs-7">Course / Programme</span>
                                <span className="fw-bolder text-gray-900 fs-7 text-end ps-2 text-truncate" title={scholarship?.course}>
                                  {scholarship?.course}
                                </span>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="p-3 bg-light rounded-3 d-flex justify-content-between align-items-center">
                                <span className="text-gray-500 fs-7">Registration / Matric Number</span>
                                <span className="fw-bolder text-primary fs-7">{scholarship?.registration_number}</span>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="p-3 bg-light rounded-3 text-center">
                                <span className="text-gray-500 fs-8 d-block mb-1">Level / Year</span>
                                <span className="fw-bolder text-gray-900 fs-5">{scholarship?.level} Lvl</span>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="p-3 bg-light rounded-3 text-center">
                                <span className="text-gray-500 fs-8 d-block mb-1">Duration</span>
                                <span className="fw-bolder text-gray-900 fs-5">{scholarship?.scholarship_duration}</span>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="p-3 bg-light rounded-3 text-center">
                                <span className="text-gray-500 fs-8 d-block mb-1">Year of Entry</span>
                                <span className="fw-bolder text-gray-900 fs-5">{scholarship?.year_of_entry}</span>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="p-3 bg-light rounded-3 text-center">
                                <span className="text-gray-500 fs-8 d-block mb-1">Graduation</span>
                                <span className="fw-bolder text-gray-900 fs-5">{scholarship?.year_of_graduation}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Disbursement History Log */}
                        <div className="d6-card p-6 flex-grow-1 d-flex flex-column">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <CardHead
                              title={`Disbursement Transaction Log (${studentPayments.length})`}
                              subtitle="Direct electronic deposits and tuition payments"
                              className="m-0"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-light-success fw-bold"
                              onClick={handleOpenDisburse}
                              disabled={scholarship?.status !== "Active"}
                            >
                              <i className="ki-outline ki-plus fs-7 me-1"></i>New Payout
                            </button>
                          </div>

                          <div className="overflow-y-auto flex-grow-1" style={{ maxHeight: 420 }}>
                            {studentPayments.length === 0 ? (
                              <div className="d6m-empty py-10">
                                <i className="ki-outline ki-wallet fs-3x text-gray-300 d-block mb-3"></i>
                                <div className="d6-title text-gray-900 mb-1">No disbursements recorded yet.</div>
                                <div className="fs-8 text-gray-500">Initiate a payout to record the first scholarship transaction.</div>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-2">
                                {studentPayments.map((p) => (
                                  <div
                                    key={p.id}
                                    className="d6-card d6-list-card d-flex align-items-center py-3 px-4 border-hover-success position-relative overflow-hidden"
                                    style={{ minHeight: 72 }}
                                  >
                                    <span className="d6m-tile me-3 flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999 }}>
                                      <i className="ki-outline ki-wallet" />
                                    </span>
                                    <div className="d-flex flex-column overflow-hidden min-w-0 me-3">
                                      <span className="fs-7 fw-bold text-gray-900 text-truncate">{p.reference || "SCH-PAY-" + p.id}</span>
                                      <span className="text-gray-500 fs-8 text-truncate">
                                        {p.notes || `${p.method || "Bank Transfer"} · ${scholarship?.bank_name}`}
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-4 text-end ms-auto flex-shrink-0">
                                      <div className="d-none d-sm-block text-center">
                                        <div className="d6m-k mb-0">Date</div>
                                        <div className="fw-semibold fs-8 text-gray-900">{fmtDate(p.date)}</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="d6m-k mb-0">Amount</div>
                                        <div className="fw-bold text-success fs-7">{fmtCurrency(p.amount)}</div>
                                      </div>
                                      <StatusChip color="#16A34A">{p.status || "Completed"}</StatusChip>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>

                    <div className="my-10"></div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                  <div className="text-gray-900 text-center">
                    <span className="text-muted fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL: Edit Scholarship Details */}
      <ModalShell
        modalRef={editModalRef}
        icon="ki-pencil"
        title="Edit Scholarship Profile"
        onClose={() => hideModal(editModalRef)}
        width={780}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(editModalRef)}>Cancel</button>
            <button type="submit" form="edit-scholarship-details-form" className="btn btn-success d6-mbtn">Save Changes</button>
          </>
        }
      >
        <div className="d6m-upload">
          <button type="button" className="d6m-upload-preview" style={{ borderRadius: 999 }} onClick={() => editFileInputRef.current?.click()} aria-label="Upload photo">
            {editForm.imageUrl ? <img src={editForm.imageUrl} alt="" style={{ borderRadius: 999 }} /> : <i className="ki-outline ki-camera"></i>}
          </button>
          <div className="flex-grow-1 min-w-0">
            <div className="d6m-v">Beneficiary Photo</div>
            <div className="d6m-muted">PNG or JPG, up to 5MB</div>
          </div>
          <div className="d-flex gap-2">
            {editForm.imageUrl && (
              <button type="button" className="btn btn-light-danger d6-mbtn" onClick={() => setEditForm({ ...editForm, imageUrl: "", imagePreview: null })}>
                Remove
              </button>
            )}
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => editFileInputRef.current?.click()}>
              {editForm.imageUrl ? "Change" : "Upload"}
            </button>
          </div>
          <input type="file" ref={editFileInputRef} className="d-none" accept="image/*" onChange={handleImageSelect} />
        </div>

        <form id="edit-scholarship-details-form" onSubmit={handleEditSubmit} className="d-flex flex-column gap-5 mt-4">

          {/* General & Academic Information */}
          <div className="card border p-4 rounded-3 shadow-none">
            <div className="mb-3 pb-2 border-bottom">
              <h6 className="fw-bolder text-gray-900 m-0">General &amp; Academic Information</h6>
            </div>
            <div className="row g-3">
              <Field className="col-12 col-md-6" label="Academic Session" required>
                <select
                  className="d6m-input"
                  value={editForm.academic_session}
                  onChange={(e) => setEditForm({ ...editForm, academic_session: e.target.value })}
                >
                  {ACADEMIC_SESSIONS_LIST.map((sess) => (
                    <option key={sess} value={sess}>{sess} Academic Session</option>
                  ))}
                </select>
              </Field>

              <Field className="col-12 col-md-6" label="Institution / School Name" required>
                <select
                  className="d6m-input"
                  value={editForm.institution_name}
                  onChange={(e) => setEditForm({ ...editForm, institution_name: e.target.value })}
                >
                  {INSTITUTIONS_LIST.map((inst) => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </select>
              </Field>

              {editForm.institution_name === "Other / External Institution" && (
                <Field className="col-12" label="Custom Institution Name" required>
                  <input
                    type="text"
                    className="d6m-input"
                    required
                    value={editForm.custom_institution_name}
                    onChange={(e) => setEditForm({ ...editForm, custom_institution_name: e.target.value })}
                  />
                </Field>
              )}

              <Field className="col-12 col-md-6" label="Course of Study / Programme" required>
                <input
                  type="text"
                  className="d6m-input"
                  required
                  value={editForm.course}
                  onChange={(e) => setEditForm({ ...editForm, course: e.target.value })}
                />
              </Field>

              <Field className="col-12 col-md-6" label="Registration / Matric Number" required>
                <input
                  type="text"
                  className="d6m-input"
                  required
                  value={editForm.registration_number}
                  onChange={(e) => setEditForm({ ...editForm, registration_number: e.target.value })}
                />
              </Field>

              <Field className="col-6 col-md-3" label="Level / Current Year" required>
                <select
                  className="d6m-input"
                  value={editForm.level}
                  onChange={(e) => setEditForm({ ...editForm, level: e.target.value })}
                >
                  {LEVELS_LIST.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl} Level</option>
                  ))}
                </select>
              </Field>

              <Field className="col-6 col-md-3" label="Scholarship Duration" required>
                <select
                  className="d6m-input"
                  value={editForm.scholarship_duration}
                  onChange={(e) => setEditForm({ ...editForm, scholarship_duration: e.target.value })}
                >
                  {SCHOLARSHIP_DURATIONS_LIST.map((dur) => (
                    <option key={dur} value={dur}>{dur}</option>
                  ))}
                </select>
              </Field>

              <Field className="col-6 col-md-3" label="Year of Entry" required>
                <input
                  type="number"
                  min="2018"
                  max="2035"
                  className="d6m-input"
                  required
                  value={editForm.year_of_entry}
                  onChange={(e) => setEditForm({ ...editForm, year_of_entry: e.target.value })}
                />
              </Field>

              <Field className="col-6 col-md-3" label="Year of Graduation" required>
                <input
                  type="number"
                  min="2020"
                  max="2040"
                  className="d6m-input"
                  required
                  value={editForm.year_of_graduation}
                  onChange={(e) => setEditForm({ ...editForm, year_of_graduation: e.target.value })}
                />
              </Field>
            </div>
          </div>

          {/* Student Bio-Data & Origin */}
          <div className="card border p-4 rounded-3 shadow-none">
            <div className="mb-3 pb-2 border-bottom">
              <h6 className="fw-bolder text-gray-900 m-0">Student Bio-Data &amp; Origin</h6>
            </div>
            <div className="row g-3">
              <Field className="col-12" label="Full Name" required>
                <input
                  type="text"
                  className="d6m-input"
                  required
                  value={editForm.student_name}
                  onChange={(e) => setEditForm({ ...editForm, student_name: e.target.value })}
                />
              </Field>

              <Field className="col-12 col-md-6" label="State of Origin" required>
                <select
                  className="d6m-input"
                  value={editForm.state_of_origin}
                  onChange={(e) => setEditForm({ ...editForm, state_of_origin: e.target.value })}
                >
                  {STATES_LIST.map((st) => (
                    <option key={st} value={st}>{st} State</option>
                  ))}
                </select>
              </Field>

              <Field className="col-12 col-md-6" label="LGA (Local Government Area)" required>
                {editForm.state_of_origin === "Katsina" ? (
                  <select
                    className="d6m-input"
                    value={editForm.lga}
                    onChange={(e) => setEditForm({ ...editForm, lga: e.target.value })}
                  >
                    {KATSINA_LGAS.map((lga) => (
                      <option key={lga} value={lga}>{lga}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="d6m-input"
                    required
                    value={editForm.lga}
                    onChange={(e) => setEditForm({ ...editForm, lga: e.target.value })}
                  />
                )}
              </Field>

              <Field className="col-12 col-md-6" label="Phone Number (Optional)">
                <input
                  type="text"
                  className="d6m-input"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Field>

              <Field className="col-12 col-md-6" label="Email Address (Optional)">
                <input
                  type="email"
                  className="d6m-input"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Field>
            </div>
          </div>

          {/* Financial & Fee Breakdown */}
          <div className="card border p-4 rounded-3 shadow-none">
            <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
              <h6 className="fw-bolder text-gray-900 m-0">Financial &amp; Fee Breakdown</h6>
              <span className="badge badge-light-success fs-8 fw-bold">Auto-Calculated Total</span>
            </div>
            <div className="row g-3">
              <Field className="col-12 col-md-6" label="Tuition Fees / Registration Fees (₦)" required>
                <input
                  type="number"
                  min="0"
                  className="d6m-input"
                  required
                  value={editForm.tuition_or_reg_fees}
                  onChange={(e) => {
                    const tuition = e.target.value;
                    const num = Number(tuition) || 0;
                    setEditForm({
                      ...editForm,
                      tuition_or_reg_fees: tuition,
                      total_amount: num + (Number(editForm.allowance) || 0)
                    });
                  }}
                />
              </Field>

              <Field className="col-12 col-md-6" label="Allowance (₦)" required>
                <input
                  type="number"
                  min="0"
                  className="d6m-input"
                  required
                  value={editForm.allowance}
                  onChange={(e) => {
                    const allowance = e.target.value;
                    const num = Number(allowance) || 0;
                    setEditForm({
                      ...editForm,
                      allowance,
                      total_amount: (Number(editForm.tuition_or_reg_fees) || 0) + num
                    });
                  }}
                />
              </Field>

              <div className="col-12">
                <div className="p-3 bg-light-success rounded-3 d-flex align-items-center justify-content-between">
                  <div>
                    <span className="fw-bold text-gray-700 d-block fs-7">Total Scholarship Amount (Tuition + Allowance)</span>
                    <span className="fs-8 text-gray-500">Auto-calculated sum</span>
                  </div>
                  <div className="fs-3 fw-bolder text-success">
                    {fmtCurrency((Number(editForm.tuition_or_reg_fees) || 0) + (Number(editForm.allowance) || 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Disbursement & Banking Details */}
          <div className="card border p-4 rounded-3 shadow-none">
            <div className="mb-3 pb-2 border-bottom">
              <h6 className="fw-bolder text-gray-900 m-0">Disbursement &amp; Banking Details</h6>
            </div>
            <div className="row g-3">
              <Field className="col-12 col-md-6" label="Bank Name" required>
                <select
                  className="d6m-input"
                  value={editForm.bank_name}
                  onChange={(e) => setEditForm({ ...editForm, bank_name: e.target.value })}
                >
                  {POPULAR_BANKS_LIST.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                  <option value="Other">Other Bank</option>
                </select>
              </Field>

              {editForm.bank_name === "Other" && (
                <Field className="col-12 col-md-6" label="Custom Bank Name" required>
                  <input
                    type="text"
                    className="d6m-input"
                    required
                    value={editForm.custom_bank_name}
                    onChange={(e) => setEditForm({ ...editForm, custom_bank_name: e.target.value })}
                  />
                </Field>
              )}

              <Field className="col-12 col-md-6" label="Account Number (10 Digits)" required>
                <input
                  type="text"
                  maxLength={10}
                  className="d6m-input"
                  required
                  value={editForm.account_number}
                  onChange={(e) => setEditForm({ ...editForm, account_number: e.target.value.replace(/\D/g, "") })}
                />
              </Field>
            </div>
          </div>

          {/* Administrative & Metadata */}
          <div className="card border p-4 rounded-3 shadow-none">
            <div className="mb-3 pb-2 border-bottom">
              <h6 className="fw-bolder text-gray-900 m-0">Administrative &amp; Metadata</h6>
            </div>
            <div className="row g-3">
              <Field className="col-12 col-md-4" label="Serial Number (S/N)">
                <input
                  type="text"
                  className="d6m-input"
                  value={editForm.serial_number}
                  onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })}
                />
              </Field>

              <Field className="col-12 col-md-8" label="Remark">
                <input
                  type="text"
                  className="d6m-input"
                  value={editForm.remark}
                  onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })}
                />
              </Field>
            </div>
          </div>

        </form>
      </ModalShell>

      {/* MODAL: Disburse Payout */}
      <ModalShell
        modalRef={disburseModalRef}
        icon="ki-wallet"
        title="Initiate Scholarship Payout"
        onClose={() => hideModal(disburseModalRef)}
        width={560}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(disburseModalRef)}>Cancel</button>
            <button type="submit" form="disburse-form" className="btn btn-success d6-mbtn">Confirm &amp; Disburse Funds</button>
          </>
        }
      >
        <form id="disburse-form" onSubmit={handleDisburseSubmit}>
          <div className="d6m-person mb-4">
            <Avatar src={scholarship?.imageUrl} name={scholarName} size={52} />
            <div className="min-w-0 flex-grow-1">
              <div className="d6m-name text-truncate">{toTitleCase(scholarName)}</div>
              <div className="d6m-muted">{scholarship?.registration_number} · {scholarship?.academic_session}</div>
            </div>
            <span className="badge badge-light-primary fw-bolder">{scholarship?.serial_number}</span>
          </div>
          <div className="d6m-kv d6m-kv-3 mb-4">
            <div className="d6m-kv-cell"><span className="d6m-k">Target Bank</span><span className="d6m-v">{scholarship?.bank_name || "Not set"}</span></div>
            <div className="d6m-kv-cell"><span className="d6m-k">Account Number</span><span className="d6m-v">{scholarship?.account_number || "Not set"}</span></div>
            <div className="d6m-kv-cell"><span className="d6m-k">Total Grant</span><span className="d6m-v text-success fw-bold">{fmtCurrency(totalAmt)}</span></div>
          </div>
          <Field label="Disbursement Amount (₦)" required hint={`Default is scheduled total grant (${fmtCurrency(totalAmt)}).`}>
            <input
              type="number"
              min="1"
              className="d6m-input"
              placeholder="e.g. 150000"
              required
              value={disburseAmount}
              onChange={(e) => setDisburseAmount(e.target.value)}
            />
          </Field>
        </form>
      </ModalShell>

      {/* MODAL: Terminate Scholarship */}
      <ModalShell
        modalRef={deleteModalRef}
        icon="ki-information-5"
        title="Terminate Scholarship?"
        onClose={() => hideModal(deleteModalRef)}
        width={520}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => hideModal(deleteModalRef)}>Cancel</button>
            <button type="button" className="btn btn-danger d6-mbtn" onClick={handleDeleteSubmit}>Yes, Terminate</button>
          </>
        }
      >
        <div className="d6m-note d6m-note-danger">
          <i className="ki-outline ki-information-5 fs-3"></i>
          <div>
            Are you sure you want to permanently delete this scholarship profile for <strong>{scholarName}</strong>? This action cannot be undone.
          </div>
        </div>
      </ModalShell>
    </>
  );
}
